import { useMemo } from 'react'

import { type TargetsByOwner } from 'components/attacks/create/targets/kubernetes/kubernetes-target-utils'
import { type K8sAutocompleteSearchOption } from 'components/services/create/target-selection/kubernetes/types'
import {
  K8sKind,
  type K8sObjectWithOwningTeamId
} from 'types/api/kubernetes/kubernetes'

function getK8sKindLabel(k8sKind: string) {
  return K8sKind[k8sKind]
    ? `${K8sKind[k8sKind]}s`
    : `${k8sKind.charAt(0).toUpperCase() + k8sKind.slice(1)}s`
}

/**
 * Takes the full list of K8s options and groups them by their section (K8s kind).
 * Additionally filters the sections to only those that are present in the current K8s deployment.
 */
export function useGetGroupedK8sOptions(
  k8sKinds: string[] | undefined,
  targetsByOwnerId: Record<string, TargetsByOwner<K8sObjectWithOwningTeamId>[]>,
  options: K8sAutocompleteSearchOption[]
) {
  // Options to be rendered within each group, represented as a Map for efficient access
  return useMemo(() => {
    const targetsByOwnerIdSet = new Set(Object.keys(targetsByOwnerId))

    // Display only the sections that are present in the K8s deployment
    const categoryNames =
      k8sKinds
        ?.filter((categoryName) => targetsByOwnerIdSet.has(categoryName))
        .map(getK8sKindLabel) ?? []

    return {
      categoryNames,
      categoryOptionsByName: categoryNames.reduce<
        Map<string, K8sAutocompleteSearchOption[]>
      >((map, categoryName) => {
        map.set(
          categoryName,
          options.filter(
            ({ section }) => getK8sKindLabel(section) === categoryName
          )
        )
        return map
      }, new Map())
    }
  }, [k8sKinds, options, targetsByOwnerId])
}
